// Phase 3.16 (beads hh-7k3) — NewPropertyScreen.
//
// case-03 / case-18: create a property plus its rooms. Voice is the primary
// path (a button into VoiceModal) but that wiring is out of scope here — this
// is the UI-form fallback: name, type, and a dynamic list of room names.
// On save:
//   POST /api/v1/properties           → create the property
//   POST /api/v1/rooms  (once per non-empty room name, scoped to that property)
// then navigate to the tree.
//
// Ported (style + flow) from designproject/files/screens-onboard.jsx
// (`NewPropertyScreen`); the room-list is added per the case-03/18 spec.

import type { JSX } from 'preact';
import { useMemo, useState } from 'preact/hooks';
import { T, Icon, IconButton, inferPropertyType, type PropertyType } from '../../tokens';
import { ScreenHeader } from '../../shell';
import type { RouterApi } from '../../router';
import { properties, rooms, humanizeError } from '../../api';
import { FormLabel, FormInput, FormFooter, FormError } from './formPrimitives';
import { PropertyTypeChips } from './PropertyTypeChips';

export interface NewPropertyScreenProps {
  readonly router: RouterApi;
}

interface RoomDraft {
  readonly key: number;
  readonly name: string;
}

function describeError(err: unknown): string {
  return humanizeError(err, 'save');
}

export function NewPropertyScreen({ router }: NewPropertyScreenProps): JSX.Element {
  const [name, setName] = useState<string>('');
  // null = пользователь не выбирал тип; на сохранении инферим из названия.
  const [type, setType] = useState<PropertyType | null>(null);
  const [roomDrafts, setRoomDrafts] = useState<readonly RoomDraft[]>([{ key: 0, name: '' }]);
  const [nextKey, setNextKey] = useState<number>(1);
  const [pending, setPending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const canSave = useMemo<boolean>(
    () => name.trim().length > 0 && !pending,
    [name, pending],
  );

  const back = (): void => router.back();

  const addRoomField = (): void => {
    setRoomDrafts((prev) => [...prev, { key: nextKey, name: '' }]);
    setNextKey((k) => k + 1);
  };

  const removeRoomField = (key: number): void => {
    setRoomDrafts((prev) => prev.filter((r) => r.key !== key));
  };

  const updateRoomField = (key: number, value: string): void => {
    setRoomDrafts((prev) => prev.map((r) => (r.key === key ? { ...r, name: value } : r)));
  };

  const save = (): void => {
    const trimmedName = name.trim();
    if (trimmedName.length === 0) return;
    setPending(true);
    setError(null);
    const roomNames = roomDrafts
      .map((r) => r.name.trim())
      .filter((n) => n.length > 0);
    // Тип: явный выбор пользователя > инференс по названию. Если ни то, ни
    // другое — НЕ дефолтим молча в apartment (aidepo-46): отправляем без type,
    // бэкенд применит свой дефолт, но мы не приписываем «дача»→apartment.
    const resolvedType: PropertyType | null = type ?? inferPropertyType(trimmedName);
    properties
      .createProperty(
        resolvedType !== null
          ? { name: trimmedName, type: resolvedType }
          : { name: trimmedName },
      )
      .then(async (created) => {
        // Rooms created sequentially: the backend rejects duplicate names
        // within one property, so order keeps the first failure clear.
        for (const roomName of roomNames) {
          await rooms.createRoom({ name: roomName, propertyId: created.id });
        }
        router.go('tree');
      })
      .catch((err: unknown) => {
        setError(describeError(err));
        setPending(false);
      });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: T.bg }}>
      <ScreenHeader
        title="Новая собственность"
        subtitle="Заполни вручную"
        onBack={back}
      />

      <div style={{ flex: 1, overflow: 'auto', padding: '4px 16px 16px' }}>
        <FormLabel style={{ padding: '6px 4px 8px' }}>Название</FormLabel>
        <FormInput
          value={name}
          onChange={setName}
          placeholder="Например: Дача в Подмосковье"
          disabled={pending}
        />

        <FormLabel style={{ marginTop: 18 }}>Тип · необязательно</FormLabel>
        <PropertyTypeChips value={type} onChange={setType} disabled={pending} />
        <div style={{ fontSize: 12, color: T.ink3, padding: '8px 2px 0' }}>
          {type === null
            ? 'Если не выбрать — определим по названию.'
            : 'Выбран вручную. Нажми ещё раз, чтобы снять выбор.'}
        </div>

        <FormLabel style={{ marginTop: 18 }}>Помещения · необязательно</FormLabel>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {roomDrafts.map((room) => (
            <div key={room.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <FormInput
                  value={room.name}
                  onChange={(v) => updateRoomField(room.key, v)}
                  placeholder="Кухня, спальня, гараж…"
                  disabled={pending}
                />
              </div>
              <IconButton
                icon="trash"
                onClick={() => removeRoomField(room.key)}
                disabled={pending || roomDrafts.length === 1}
              />
            </div>
          ))}
        </div>
        <button
          type="button"
          disabled={pending}
          onClick={addRoomField}
          style={{
            marginTop: 10,
            padding: '10px 14px',
            background: T.surface,
            border: `1px dashed ${T.borderStrong}`,
            borderRadius: 12,
            fontFamily: T.font,
            fontSize: 13,
            fontWeight: 650,
            color: T.brandDeep,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Icon name="plus" size={15} color={T.brandDeep} />
          Добавить помещение
        </button>

        {error !== null && <FormError message={error} />}
      </div>

      <FormFooter
        onCancel={back}
        onConfirm={save}
        confirmLabel="Создать"
        confirmIcon="plus"
        disabled={!canSave}
        pending={pending}
      />
    </div>
  );
}
